import React, { useState, useEffect } from 'react';
import {
  BarChart, Bar,
  XAxis, YAxis,
  CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, Cell,
  PieChart, Pie
} from 'recharts';
import DemographicsModel from '../models/DemographicsModel';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d'];

const DemographicsSQ = () => {
  const [demographics, setDemographics] = useState([]);
  const [ageDistribution, setAgeDistribution] = useState([]);
  const [countries, setCountries] = useState([]);
  const [genderDistribution, setGenderDistribution] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [demographicsData, ageData, countriesData, genderData] = await Promise.all([
          DemographicsModel.getDemographics(),
          DemographicsModel.getAgeDistribution(),
          DemographicsModel.getCountries(),
          DemographicsModel.getGenderDistribution()
        ]);

        // Sort age distribution data by age range
        const sortedAgeData = [...ageData].sort((a, b) => {
          const getAgeValue = (range) => {
            const [min] = range.split('-').map(Number);
            return min || 0; // Handle '55+' case
          };
          return getAgeValue(a.age_range) - getAgeValue(b.age_range);
        });

        // Sort countries data by count in descending order
        const sortedCountriesData = [...countriesData].sort((a, b) => b.count - a.count);

        setDemographics(demographicsData);
        setAgeDistribution(sortedAgeData);
        setCountries(sortedCountriesData);
        setGenderDistribution(genderData);
        setError(null);
      } catch (error) {
        console.error('Error fetching data:', error);
        setError('Failed to load data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return <div className="flex justify-center items-center h-64">Loading...</div>;
  }

  if (error) {
    return <div className="text-red-500 text-center p-4">{error}</div>;
  }

  return (
    <div className="space-y-6">
      {/* Demographics Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {demographics.map((item, index) => (
          <div key={index} className="bg-white p-4 rounded shadow">
            <h3 className="text-sm font-medium text-gray-500">{item.category}</h3>
            <p className="text-2xl font-bold">{item.value}</p>
          </div>
        ))}
      </div>

      {/* Age Distribution */}
      <div className="bg-white p-4 rounded shadow">
        <h3 className="text-base font-semibold mb-4">Age Distribution</h3>
        <div style={{ width: '100%', height: 300 }}>
          <ResponsiveContainer>
            <BarChart data={ageDistribution}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="age_range" 
                label={{ value: 'Age Range', position: 'insideBottom', offset: -5 }}
              />
              <YAxis 
                label={{ value: 'Number of Cases', angle: -90, position: 'insideLeft' }}
              />
              <Tooltip />
              <Legend />
              <Bar 
                dataKey="count" 
                fill="#8884d8" 
                name="Number of Cases"
                label={{ position: 'top' }}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Gender Distribution and Top Countries */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Gender Distribution */}
        <div className="bg-white p-4 rounded shadow">
          <h3 className="text-base font-semibold mb-4">Gender Distribution</h3>
          <div style={{ width: '100%', height: 300 }}>
            <ResponsiveContainer>
              <PieChart>
                <Pie
                  data={genderDistribution}
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="count"
                  nameKey="gender"
                  label={({ gender, percent }) => `${gender}: ${(percent * 100).toFixed(0)}%`}
                >
                  {genderDistribution.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Top Countries */}
        <div className="bg-white p-4 rounded shadow">
          <h3 className="text-base font-semibold mb-4">Top Countries</h3>
          <div style={{ width: '100%', height: 300 }}>
            <ResponsiveContainer>
              <BarChart layout="vertical" data={countries}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  type="number" 
                  label={{ value: 'Number of Cases', position: 'insideBottom', offset: -5 }}
                />
                <YAxis 
                  type="category" 
                  dataKey="country" 
                  width={100}
                />
                <Tooltip />
                <Legend />
                <Bar 
                  dataKey="count" 
                  fill="#82ca9d" 
                  name="Number of Cases"
                  label={{ position: 'right' }}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DemographicsSQ; 